<template>
  <div class="observed-behavior">
    <div
      v-for="type in visibleTypes"
      :key="type"
      class="observed-behavior__row"
    >
      <div class="observed-behavior__controls">
        <label class="observed-behavior__label">{{ typeTitle(type) }}:</label>
        <template v-if="!isReadOnly">
          <validation-provider
            v-slot="{ errors }"
            :rules="isSelectedPickerOther(type) ? 'required' : ''"
          >
            <custom-drop-down
              v-model="selectedByType[type].option"
              custom-error-msg="El campo es requerido"
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="availableOptions(type)"
              placeholder="Seleccione"
            />
            <custom-input
              v-if="isSelectedPickerOther(type)"
              v-model="selectedByType[type].other"
              class="mt-3"
              custom-error-msg="El campo es requerido"
              :error="errors[0]"
              maxlength="250"
              placeholder="Especifique"
            />
          </validation-provider>
          <div class="observed-behavior__actions">
            <action-button
              i="fas fa-file-plus"
              text="Añadir"
              variant="success"
              @click="addOption(type)"
            />
          </div>
          <error
            v-if="showOtherError(type)"
            message="Debe especificar la opción Otro."
            variant="no-border"
          />
          <error
            v-if="showOptionsError(type)"
            message="Debe seleccionar al menos uno."
            variant="no-border"
          />
        </template>
      </div>
      <div class="observed-behavior__selected">
        <span class="observed-behavior__selected-label text-muted">
          Seleccionado(s):
        </span>
        <div
          v-if="selectedOptions(type).length"
          class="observed-behavior__chips"
        >
          <button
            v-for="item in selectedOptions(type)"
            :key="item.id"
            class="observed-behavior__chip"
            :disabled="isReadOnly"
            type="button"
            @click="removeOption(item)"
          >
            <span>{{ optionLabel(item) }}</span>
            <i v-if="!isReadOnly" class="far fa-times-circle" />
          </button>
        </div>
        <span v-else class="observed-behavior__empty">-</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import Error from 'custom-error';

const createSelectedByType = (types) =>
  (types || []).reduce(
    (result, type) => ({
      ...result,
      [type]: { option: null, other: null },
    }),
    {}
  );

export default {
  name: 'ObservedBehaviorSelector',
  components: {
    ActionButton,
    CustomDropDown,
    CustomInput,
    Error,
    ValidationProvider,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    types: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      required: true,
    },
    validated: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      selectedByType: createSelectedByType(this.types),
    };
  },
  computed: {
    options() {
      return this.value || [];
    },
    normalizedTypes() {
      return this.types || [];
    },
    visibleTypes() {
      return this.normalizedTypes.filter(
        (type) => this.typeOptions(type).length
      );
    },
    showOptionsError() {
      return (type) => {
        if (this.isReadOnly) return false;
        const options = this.typeOptions(type);
        return (
          this.validated && options.length && !options.some((x) => x.checked)
        );
      };
    },
    showOtherError() {
      return (type) => {
        const selected = this.selectedOptions(type);
        return (
          this.validated &&
          selected.some((item) => this.isOtherOption(item) && !item.other)
        );
      };
    },
  },
  watch: {
    types: {
      handler(value) {
        this.selectedByType = createSelectedByType(value);
      },
      deep: true,
    },
  },
  methods: {
    typeOptions(type) {
      return this.options.filter((option) => option.type?.id === type);
    },
    typeTitle(type) {
      const option = this.typeOptions(type)[0];
      return option?.type?.name || '';
    },
    availableOptions(type) {
      return this.typeOptions(type).filter((option) => !option.checked);
    },
    selectedOptions(type) {
      return this.typeOptions(type).filter((option) => option.checked);
    },
    selectedPicker(type) {
      this.ensureTypePicker(type);
      const picker = this.selectedByType[type];
      const option = picker?.option;
      if (!option) return null;
      return {
        ...option,
        isOther: this.isOtherOption(option),
      };
    },
    isSelectedPickerOther(type) {
      return this.selectedPicker(type)?.isOther || false;
    },
    isOtherOption(option) {
      const name = option?.name?.toLowerCase().trim();
      return option?.isOther || ['otro', 'otros'].includes(name);
    },
    optionLabel(option) {
      if (this.isOtherOption(option) && option.other) return option.other;
      return option.name;
    },
    addOption(type) {
      this.ensureTypePicker(type);
      const picker = this.selectedByType[type];
      const option = picker?.option;
      if (!option) return;

      const isOther = this.isOtherOption(option);
      if (isOther && !picker.other) return;

      const optionToUpdate = this.typeOptions(type).find(
        (x) => x.id === option.id
      );
      if (!optionToUpdate) return;

      optionToUpdate.checked = true;
      optionToUpdate.other = isOther ? picker.other : null;
      picker.option = null;
      picker.other = null;
      this.$emit('input', this.options);
    },
    ensureTypePicker(type) {
      if (this.selectedByType[type]) return;
      this.$set(this.selectedByType, type, { option: null, other: null });
    },
    removeOption(option) {
      option.checked = false;
      option.other = null;
      this.$emit('input', this.options);
    },
    validate() {
      return this.visibleTypes.every((type) => {
        const selected = this.selectedOptions(type);
        return (
          selected.length > 0 &&
          selected.every(
            (option) => !this.isOtherOption(option) || option.other
          )
        );
      });
    },
  },
};
</script>

<style scoped lang="scss">
.observed-behavior {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.observed-behavior__row {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(280px, 1fr);
  gap: 28px;
  padding: 24px;
  background: #f7f7f7;
  border: 1px solid #eeeeee;
  border-radius: 4px;
}

.observed-behavior__controls {
  padding-right: 28px;
  border-right: 1px solid #b5c3bc;
}

.observed-behavior__label,
.observed-behavior__selected-label {
  display: block;
  margin-bottom: 8px;
  color: #11291b;
  font-family: Lato;
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
}

.observed-behavior__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.observed-behavior__selected {
  min-width: 0;
}

.observed-behavior__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.observed-behavior__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  max-width: 100%;
  padding: 8px 18px;
  color: #11291b;
  font-family: Lato;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  text-align: left;
  word-break: break-word;
  background: #fdfdfc;
  border: 1px solid #cfd8d3;
  border-radius: 20px;
}

.observed-behavior__chip:disabled {
  color: #3f5b60;
  cursor: default;
}

.observed-behavior__chip i {
  flex: 0 0 auto;
  color: #dc3545;
}

.observed-behavior__empty {
  color: #727272;
  font-size: 14px;
}

@media (max-width: 991.98px) {
  .observed-behavior__row {
    grid-template-columns: 1fr;
  }

  .observed-behavior__controls {
    padding-right: 0;
    padding-bottom: 20px;
    border-right: 0;
    border-bottom: 1px solid #b5c3bc;
  }
}
</style>
